import {
  BackfillRecipeDto,
  FillType,
  OptimisationTargetType,
  PourNoteRoutePointsDto,
  RheologyDataDto,
  ThroughputControlType
} from 'providers/api';
import { isNumber } from 'utils';
import { calculateFrictionForRoute } from '../ProjectAreaPage/ProjectStopesPage/canvas/Model/updateFriction';
import { updateRecipeState } from '../ProjectAreaPage/ProjectStopesPage/canvas/Model/updateRecipeState';
import { Model } from '../ProjectAreaPage/ProjectStopesPage/hooks/useModelState';
import PCRoute from '../ProjectAreaPage/ProjectStopesPage/MineModel/Route/PCRoute';
import { generateAllRoutes, routeOptsForProject, returnShortestRoute } from '../ProjectAreaPage/ProjectStopesPage/MineModel/Route/utils';
import { RecipeState } from '../ProjectAreaPage/ProjectStopesPage/MineModel/types';
import { findStoredRoute } from '../ProjectAreaPage/ProjectStopesPage/StopePanelPage/recipeModelUtils';

// Builds the nominal recipe state for a recipe's selected specification, mirroring
// applyRecipeModelVisuals so the friction calc matches what the single pour produces.
const buildRecipeState = (
  model: Model,
  recipe: BackfillRecipeDto,
  rheologyDataSet: RheologyDataDto,
  fillType: FillType,
): RecipeState | undefined => {
  const base: RecipeState = {
    ...(model.recipeState ?? {} as RecipeState),
    rheologyDataSetId: rheologyDataSet.rheologyDataSetId,
  };

  if (fillType === FillType.Paste && recipe.pasteSelectedSpecification) {
    const massConc = recipe.pasteSelectedSpecification.massConcentration;
    const binderConc = recipe.pasteSelectedSpecification.binderContent;
    if (!isNumber(massConc) || !isNumber(binderConc)) return undefined;

    return updateRecipeState(
      model,
      base,
      massConc / 100,
      binderConc / 100,
      rheologyDataSet?.tailingsDrySolidsDensity,
      rheologyDataSet?.binderDryParticleDensity,
      rheologyDataSet?.carrierFluidDensity,
      recipe.pasteSpecification?.throughputControlType === ThroughputControlType.DryTonnage ? recipe.pasteSpecification?.throughput : undefined,
      recipe.pasteSpecification?.throughputControlType === ThroughputControlType.FlowRate ? recipe.pasteSpecification?.throughput : undefined,
      recipe.pasteSpecification?.throughputControlType === ThroughputControlType.WetTonnage ? recipe.pasteSpecification?.throughput : undefined,
      recipe.pasteSelectedSpecification?.pumpPressure,
      recipe.pasteSelectedSpecification?.ucsStrength,
    );
  }

  if (fillType === FillType.Hydraulic && recipe.hydraulicSelectedSpecification) {
    const massConc = recipe.hydraulicSelectedSpecification.massConcentration;
    const binderRatio = recipe.hydraulicSpecification?.binderTailingsRatio ?? 0.02;
    if (!massConc) return undefined;

    return updateRecipeState(
      model,
      base,
      massConc / 100,
      binderRatio,
      rheologyDataSet?.tailingsDrySolidsDensity,
      rheologyDataSet?.binderDryParticleDensity,
      rheologyDataSet?.carrierFluidDensity,
      recipe.hydraulicSpecification?.throughputControlType === ThroughputControlType.DryTonnage ? recipe.hydraulicSpecification?.throughput : undefined,
      recipe.hydraulicSpecification?.throughputControlType === ThroughputControlType.WetTonnage ? recipe.hydraulicSpecification?.throughput : undefined,
      recipe.hydraulicSpecification?.throughputControlType === ThroughputControlType.FlowRate ? recipe.hydraulicSpecification?.throughput : undefined,
    );
  }

  return undefined;
};

export interface PourRouteSolution {
  route: PCRoute;
  recipeState: RecipeState;
  slackPressures: Record<string, number>;
  maxPressures: Record<string, number>;
  slackSlurryDensity?: number;
  maxSlurryDensity?: number;
  flowTargetPressures: { flowRate: number; pressures: Record<string, number> }[];
}

// Resolves a pour's recipe-specific route from its spool lineup and computes pressures the same
// way a single pour does. Nominal pressures are left applied to the route on return; the
// slack/max/target passes are reported separately because each overwrites the route in place.
export const buildPourRouteSolution = (
  model: Model,
  stope: any,
  recipe: BackfillRecipeDto,
  rheologyDataSet: RheologyDataDto,
  fillType: FillType,
): PourRouteSolution | undefined => {
  const spoolIds = recipe.spoolIds ?? [];
  const plantNodeId = spoolIds[spoolIds.length - 1];
  if (spoolIds.length <= 1 || !plantNodeId || !model.nodes[plantNodeId]) return undefined;

  const shortestLiveRoute = (): PCRoute | undefined => {
    try {
      return returnShortestRoute(generateAllRoutes(
        model.nodes[plantNodeId],
        model.spools,
        stope,
        Object.values(model.pipes),
        routeOptsForProject(model.project),
        model.nodes,
        model.pipes,
      ).routeObjects);
    } catch {
      return undefined;
    }
  };

  // A pour keeps the route it was planned on, even over a spool deactivated since. Once that route no longer
  // exists the shortest live route stands in, mirroring applyRecipeModelVisuals: without it the pour has no route
  // points, which surfaces as a stuck "route data unavailable" warning even though the stope page renders a route.
  const route = findStoredRoute(model, spoolIds, recipe.routeSpoolIds, stope) ?? shortestLiveRoute();
  if (!route) return undefined;

  const recipeState = buildRecipeState(model, recipe, rheologyDataSet, fillType);
  if (!recipeState) return undefined;

  // Nominal pressures mutate route.routePoints in place.
  calculateFrictionForRoute(model, recipeState, route);

  const slackPressures: Record<string, number> = {};
  const maxPressures: Record<string, number> = {};
  const flowTargetPressures: { flowRate: number; pressures: Record<string, number> }[] = [];
  let slackSlurryDensity: number | undefined;
  let maxSlurryDensity: number | undefined;

  if (fillType === FillType.Paste) {
    if (recipe.pasteSlackFlowSpecification) {
      const slackState = updateRecipeState(
        model,
        recipeState,
        recipe.pasteSlackFlowSpecification.massConcentration / 100,
        recipe.pasteSlackFlowSpecification.binderContent / 100,
        rheologyDataSet?.tailingsDrySolidsDensity,
        rheologyDataSet?.binderDryParticleDensity,
        rheologyDataSet?.carrierFluidDensity,
      );
      calculateFrictionForRoute(model, slackState, route);
      slackSlurryDensity = slackState.wetDensity;
      route.routePoints.forEach((rp) => { slackPressures[rp.point.id] = rp.pressure ?? 0; });
    }

    if (recipe.pasteMaxPressureSpecification) {
      const maxState = updateRecipeState(
        model,
        recipeState,
        recipe.pasteMaxPressureSpecification.massConcentration / 100,
        recipe.pasteMaxPressureSpecification.binderContent / 100,
        rheologyDataSet?.tailingsDrySolidsDensity,
        rheologyDataSet?.binderDryParticleDensity,
        rheologyDataSet?.carrierFluidDensity,
      );
      calculateFrictionForRoute(model, maxState, route);
      maxSlurryDensity = maxState.wetDensity;
      route.routePoints.forEach((rp) => { maxPressures[rp.point.id] = rp.pressure ?? 0; });
    }

    // Flow rate targets: same mix as the optimised recipe, different throughput. Only pressures move.
    const optimised = recipe.pasteOptimisedSpecification;
    if (optimised) {
      (recipe.targetLines ?? [])
        .filter((tl) => tl.targetType === OptimisationTargetType.FlowRate && (tl.flowRateTarget ?? 0) > 0)
        .forEach((tl) => {
          const flowRate = tl.flowRateTarget as number;
          const targetState = updateRecipeState(
            model,
            { ...recipeState, throughputControlType: ThroughputControlType.FlowRate, wetFlowRate: flowRate },
            optimised.massConcentration / 100,
            optimised.binderContent / 100,
            rheologyDataSet?.tailingsDrySolidsDensity,
            rheologyDataSet?.binderDryParticleDensity,
            rheologyDataSet?.carrierFluidDensity,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            ThroughputControlType.FlowRate,
          );
          calculateFrictionForRoute(model, targetState, route);
          const pressures: Record<string, number> = {};
          route.routePoints.forEach((rp) => { pressures[rp.point.id] = rp.pressure ?? 0; });
          flowTargetPressures.push({ flowRate, pressures });
        });
    }

    // Restore nominal pressures after the slack/max/target passes mutated them.
    calculateFrictionForRoute(model, recipeState, route);
  }

  return {
    route,
    recipeState,
    slackPressures,
    maxPressures,
    slackSlurryDensity,
    maxSlurryDensity,
    flowTargetPressures,
  };
};

// Order is left as-is — the backend reverses per RouteNodeOrder, matching the single-pour flow.
export const toPourRoutePoints = ({
  route, slackPressures, maxPressures, flowTargetPressures,
}: PourRouteSolution): PourNoteRoutePointsDto[] => route.routePoints.map((rp) => ({
  distanceFromPump: rp.distanceFromPump ?? 0,
  pipeDownstreamName: rp.pipeDownstream?.name ?? '',
  pipeUpstreamName: rp.pipeUpstream?.name ?? '',
  pointId: rp.point.id,
  point: { x: rp.point.coords.x, y: rp.point.coords.y, z: rp.point.coords.z },
  pressure: rp.pressure ?? 0,
  minPressure: slackPressures[rp.point.id],
  maxPressure: maxPressures[rp.point.id],
  pitName: rp.point.pit ?? '',
  targetPressures: flowTargetPressures.map((target) => ({
    flowRate: target.flowRate,
    pressure: target.pressures[rp.point.id],
  })),
}));

const buildPourRoutePoints = (
  model: Model,
  stope: any,
  recipe: BackfillRecipeDto,
  rheologyDataSet: RheologyDataDto,
  fillType: FillType,
): PourNoteRoutePointsDto[] => {
  const solution = buildPourRouteSolution(model, stope, recipe, rheologyDataSet, fillType);
  return solution ? toPourRoutePoints(solution) : [];
};

export default buildPourRoutePoints;
